import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { STANDARD_DIMENSIONS } from './dimensions';
import { BehaviorSubject } from 'rxjs';
import { User } from 'src/app/models/user.interface';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CollectionContext } from 'src/app/models/collection.interface';

@Component({
  selector: 'app-add-collection-dialog',
  templateUrl: './add-collection-dialog.component.html',
  styleUrls: ['./add-collection-dialog.component.scss']
})
export class AddCollectionDialogComponent implements OnInit {
  addCollectionForm!: FormGroup;
  users: User[] = [];
  initialDimensions = STANDARD_DIMENSIONS;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  private filteredUsersSubject = new BehaviorSubject<{ user_id: string; email: string }[]>([]);
  filteredUsers$ = this.filteredUsersSubject.asObservable();
  searchControl = new FormControl();

  // Need ref to clear the input field on mouse click
  @ViewChild('chipInput') chipInput!: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<AddCollectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddCollectionDialogComponent,
    private fb: FormBuilder
  ) {
    this.users = data.users;
  }

  ngOnInit(): void {
    this.addCollectionForm = this.fb.group({
      name: new FormControl(''),
      description: new FormControl(''),
      context: new FormControl(CollectionContext.REGULATORY),
      is_private: new FormControl(false),
      access_list: new FormArray([]),
      dimensions: new FormArray(
        this.initialDimensions.map(dim =>
          this.fb.group({
            name: new FormControl({ value: dim['name'], disabled: true }),
            description: new FormControl({ value: dim['description'], disabled: true }),
            show_advisor_filter: new FormControl({ value: dim['show_advisor_filter'], disabled: false })
          })
        )
      )
    });

    this.searchControl.valueChanges.subscribe(value => {
      this.filteredUsersSubject.next(this._filterUsers(value));
    });
  }

  get isDirty() {
    // Ensure name is not empty when adding new collection
    return !this.addCollectionForm.get('name')?.value;
  }

  displayUser(user: { user_id: string; email: string }): string {
    return user ? user.email : '';
  }

  addAccessFromInput(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value.trim();

    if (value) {
      const user = this.users.find(u => u.email.toLowerCase() === value.toLowerCase());
      if (user && !this.accessList.value.includes(user.user_id)) {
        this.accessList.push(new FormControl(user.user_id));
      }
    }

    // Clear the input field and reset the search control
    if (input) {
      input.value = '';
    }
    this.searchControl.setValue(null);
  }

  get accessList(): FormArray {
    return this.addCollectionForm.get('access_list') as FormArray;
  }

  get dimensions(): FormArray {
    return this.addCollectionForm.get('dimensions') as FormArray;
  }

  private _filterUsers(value: string): { user_id: string; email: string }[] {
    // Ensure the value is a string
    const filterValue = value ? value.toString().toLowerCase() : '';
    return this.users.filter(user => user.email.toLowerCase().includes(filterValue));
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  saveForm(): void {
    this.dimensions.controls.forEach(control => control.enable());
    const formValue = this.addCollectionForm.value;
    this.dialogRef.close(formValue);
  }

  addAccess(event: MatAutocompleteSelectedEvent): void {
    const user: User = event.option.value;
    if (!this.accessList.value.includes(user.user_id)) {
      this.accessList.push(new FormControl(user.user_id));
    }

    this.searchControl.setValue('');
    if (this.chipInput) {
      this.chipInput.nativeElement.value = '';
    }
  }

  removeAccess(user: string): void {
    const index = this.accessList.controls.findIndex(control => control.value === user);
    if (index >= 0) {
      this.accessList.removeAt(index);
    }
  }

  addDimension(): void {
    this.dimensions.insert(
      0,
      this.fb.group({
        name: new FormControl(''),
        description: new FormControl('')
      })
    );
  }

  removeDimension(index: number): void {
    this.dimensions.removeAt(index);
  }

  getEmail(user_id: string): string | undefined {
    return this.users.find(user => user.user_id === user_id)?.email;
  }
}
